import React, { useState } from 'react';
import axios from 'axios'; // Import Axios
import { motion } from 'framer-motion';
import { Mail, Check, ChevronRight, Building, Users, MessageSquare } from 'lucide-react';
import './ContactPage.css';

// const contactMethods = [
//     {
//         icon: Mail,
//         title: "Email Us",
//         description: "Email us for general inquiries",
//         action: "Send Email",
//         link: "mailto:info@ufficient.ai"
//     },
// ];

const industries = [
    "Engineering & Construction",
    "Professional Services",
    "Manufacturing",
    "Research & Development",
    "Technology",
    "Other"
];

const ContactPage = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        industry: '',
        employees: '',
        message: ''
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        try {
            // Prepare data expected by backend Lambda (snake_case keys)
            const payload = {
                first_name: formData.firstName,
                last_name: formData.lastName,
                company: formData.company,
                industry: formData.industry,
                email: formData.email,
                message: formData.message,
            };

            const response = await axios.post(
                'https://jbuyfsfyd0.execute-api.ca-central-1.amazonaws.com/default/contact-us',
                payload,
                {
                    headers: { 'Content-Type': 'application/json' },
                }
            );
            if (response.status === 200) {
                setIsSubmitted(true);
                setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    company: '',
                    industry: '',
                    employees: '',
                    message: ''
                });
                setTimeout(() => setIsSubmitted(false), 3000);
            }
        } catch (err) {
            console.error(err);
            setError('There was an error submitting the form. Please try again later.');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const featuresData = [
        {
            icon: Building,
            title: "For Engineering & Construction",
            description: "Learn how our platform can streamline your project documentation, automate RFP responses, and manage engineering requirements."
        },
        {
            icon: Users,
            title: "For Enterprise Teams",
            description: "Schedule a consultation to see how Ufficient can transform your organization's knowledge management and team collaboration."
        },
        {
            icon: MessageSquare,
            title: "Customer Support",
            description: "Current customers can get help with any questions or technical issues through our dedicated support team."
        }
    ];

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                type: "spring",
                stiffness: 50
            }
        }
    };

    return (
        <div className="contact-page">
            {/* Hero Section */}
            <motion.div 
                className="contact-hero"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                <div className="glass-effect hero-glass"></div>
                <motion.h1 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    className="contact-title"
                >
                    Get in <span className="gradient-text">Touch</span>
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="contact-description"
                >
                    Let's discuss how Ufficient can transform your organization's knowledge management
                </motion.p>
            </motion.div>

            {/* Contact Features */}
            <section className="contact-features">
                <div className="container">
                    <motion.div 
                        className="features-grid"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {featuresData.map((feature, index) => (
                            <motion.div 
                                key={index}
                                className="feature-card"
                                variants={itemVariants}
                                whileHover={{ 
                                    y: -8, 
                                    boxShadow: "0 10px 25px rgba(0, 120, 255, 0.15)",
                                    transition: { duration: 0.2 } 
                                }}
                            >
                                <div className="feature-icon-container">
                                    <feature.icon className="feature-icon" />
                                </div>
                                <h3>{feature.title}</h3>
                                <p>{feature.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Contact Form */}
            <section className="contact-form-section">
                <div className="glass-effect form-glass"></div>
                <div className="container">
                    <div className="form-content">
                        <motion.div 
                            className="form-header"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2>Tell us about your needs</h2>
                            <p>Fill out the form and we'll get back to you within 24 hours</p>
                        </motion.div>

                        <motion.form 
                            className="contact-form"
                            onSubmit={handleSubmit}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                        >
                            <div className="form-grid">
                                <div className="form-group">
                                    <input
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        placeholder="First Name"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        required
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        placeholder="Last Name"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        required
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        placeholder="Work Email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <input
                                        type="text"
                                        id="company"
                                        name="company"
                                        placeholder="Company"
                                        value={formData.company}
                                        onChange={handleChange}
                                        required
                                        className="form-input"
                                    />
                                </div>
                                <div className="form-group">
                                    <select
                                        id="industry"
                                        name="industry"
                                        value={formData.industry}
                                        onChange={handleChange}
                                        required
                                        className="form-select"
                                    >
                                        <option value="">Select Industry</option>
                                        {industries.map((industry, index) => (
                                            <option key={index} value={industry}>
                                                {industry}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <select
                                        id="employees"
                                        name="employees"
                                        value={formData.employees}
                                        onChange={handleChange}
                                        required
                                        className="form-select"
                                    >
                                        <option value="">Select Company Size</option>
                                        <option value="1-10">1-10 employees</option>
                                        <option value="11-50">11-50 employees</option>
                                        <option value="51-200">51-200 employees</option>
                                        <option value="201-500">201-500 employees</option>
                                        <option value="501+">501+ employees</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group full-width">
                                <textarea
                                    id="message"
                                    name="message"
                                    placeholder="Your Message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    rows={4}
                                    required
                                    className="form-textarea"
                                />
                            </div>
                            <motion.button
                                type="submit"
                                className="submit-button"
                                whileHover={{ scale: 1.02, boxShadow: "0 5px 15px rgba(0, 120, 255, 0.4)" }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <span>Send Message</span>
                                <ChevronRight size={20} />
                            </motion.button>
                        </motion.form>

                        {error && (
                            <motion.div 
                                className="error-message"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <p>{error}</p>
                            </motion.div>
                        )}

                        <motion.div 
                            className={`submission-message ${isSubmitted ? 'show' : ''}`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: isSubmitted ? 1 : 0, y: isSubmitted ? 0 : 10 }}
                        >
                            <Check size={20} />
                            Message sent successfully! We'll be in touch soon.
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ContactPage;
